// ============================================================
// HostingV3 — shared presentational components.
//
// These are PURE display primitives. They hold no fake runtime
// state. Every piece of data they render comes from props that
// the real Hosting surface supplies from API hooks.
// ============================================================

import { useState, type ReactNode } from 'react'
import { V3 } from './tokens'

// ----------------------------------------------------------------
// Section — labeled block wrapper.
// ----------------------------------------------------------------

export function Section({
  label,
  color,
  children,
}: {
  label: string
  color: string
  children: ReactNode
}) {
  return (
    <div style={{ marginBottom: 32 }}>
      <div
        style={{
          fontSize: 11,
          letterSpacing: 4,
          color,
          fontFamily: V3.mono,
          fontWeight: 700,
          marginBottom: 14,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <div
          style={{
            width: 16,
            height: 2,
            background: color,
            opacity: 0.4,
            borderRadius: 1,
          }}
        />
        {label}
      </div>
      {children}
    </div>
  )
}

// ----------------------------------------------------------------
// ActionButton — premium action control. Handles firing state + done state.
// Used for real operator actions like "Deploy to Production".
// ----------------------------------------------------------------

export function ActionButton({
  icon,
  label,
  sub,
  color,
  onClick,
  disabled,
  loading,
  apiPath,
  method,
}: {
  icon: string
  label: string
  sub?: string
  color: string
  onClick?: () => void | Promise<void>
  disabled?: boolean
  loading?: boolean
  apiPath?: string
  method?: string
}) {
  const [hov, setHov] = useState(false)
  const [done, setDone] = useState(false)
  const firing = loading ?? false

  async function fire() {
    if (disabled || firing) return
    try {
      await onClick?.()
      setDone(true)
      setTimeout(() => setDone(false), 3000)
    } catch {
      // Error display is the caller's responsibility
    }
  }

  const lit = hov || firing
  return (
    <button
      type="button"
      onClick={fire}
      disabled={disabled}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding: '18px 20px',
        borderRadius: 14,
        cursor: disabled ? 'not-allowed' : 'pointer',
        flex: 1,
        background: firing
          ? color + '0c'
          : lit
          ? color + '06'
          : V3.pur + '02',
        border: '1px solid ' + (lit ? color + '30' : V3.pur + '08'),
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        position: 'relative',
        overflow: 'hidden',
        boxShadow: firing
          ? '0 0 30px ' + color + '12, inset 0 0 16px ' + color + '04'
          : lit
          ? '0 0 12px ' + color + '06'
          : 'none',
        transition: 'all 0.4s',
        opacity: disabled ? 0.45 : 1,
      }}
    >
      {firing && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: 2,
            background:
              'linear-gradient(90deg, transparent 15%, ' +
              color +
              ' 50%, transparent 85%)',
            animation: 'hv3ScanX 1.2s ease-out infinite',
          }}
        />
      )}
      <div
        style={{
          width: 42,
          height: 42,
          borderRadius: 11,
          flexShrink: 0,
          background: lit ? color + '15' : V3.pur + '06',
          border: '1px solid ' + (lit ? color + '30' : V3.pur + '10'),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 18,
          color: lit ? color : V3.w3,
          boxShadow: lit ? '0 0 14px ' + color + '18' : 'none',
          transition: 'all 0.4s',
        }}
      >
        {icon}
      </div>
      <div style={{ textAlign: 'left', flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: lit ? V3.w : V3.w2 }}>
          {label}
        </div>
        {sub && (
          <div style={{ fontSize: 11, color: V3.w3, marginTop: 2 }}>{sub}</div>
        )}
        {apiPath && (
          <div
            style={{
              fontSize: 9,
              fontFamily: V3.mono,
              color: V3.pur,
              marginTop: 3,
              opacity: 0.5,
            }}
          >
            {method || 'POST'} {apiPath}
          </div>
        )}
      </div>
      {firing && (
        <span
          style={{
            fontSize: 10,
            fontFamily: V3.mono,
            fontWeight: 700,
            color,
            letterSpacing: 2,
            animation: 'hv3Pulse 0.5s ease infinite',
          }}
        >
          EXECUTING
        </span>
      )}
      {done && !firing && (
        <span
          style={{
            fontSize: 10,
            fontFamily: V3.mono,
            fontWeight: 700,
            color: V3.grn,
            letterSpacing: 1,
          }}
        >
          ✓ DONE
        </span>
      )}
    </button>
  )
}

// ----------------------------------------------------------------
// StepCard — expandable walkthrough step. Instructional content.
// Status is passed in so live data can drive visible state.
// ----------------------------------------------------------------

export function StepCard({
  step,
  title,
  desc,
  apiPath,
  method,
  status,
  defaultOpen,
  children,
}: {
  step: string
  title: string
  desc: string
  apiPath?: string
  method?: string
  status?: 'done' | 'pending' | 'ready' | 'unavailable'
  defaultOpen?: boolean
  children: ReactNode
}) {
  const [open, setOpen] = useState(!!defaultOpen)
  const sc =
    status === 'done'
      ? V3.grn
      : status === 'pending'
      ? V3.gld
      : status === 'unavailable'
      ? V3.w3
      : V3.pur

  return (
    <div
      style={{
        borderRadius: 14,
        background: V3.pur + '02',
        border: '1px solid ' + V3.pur + '08',
        marginBottom: 8,
        overflow: 'hidden',
      }}
    >
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: '16px 20px',
          cursor: 'pointer',
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: sc + '12',
            border: '1px solid ' + sc + '20',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            fontFamily: V3.mono,
            fontWeight: 800,
            color: sc,
          }}
        >
          {step}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 500, color: V3.w }}>{title}</div>
          <div style={{ fontSize: 11, color: V3.w3, marginTop: 2 }}>{desc}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {apiPath && (
            <span
              style={{
                fontSize: 8,
                fontFamily: V3.mono,
                color: V3.pur,
                padding: '2px 8px',
                borderRadius: 4,
                background: V3.pur + '06',
              }}
            >
              {method || 'POST'} {apiPath}
            </span>
          )}
          <span
            style={{
              fontSize: 10,
              fontFamily: V3.mono,
              fontWeight: 700,
              color: sc,
              letterSpacing: 1,
            }}
          >
            {status?.toUpperCase() || 'READY'}
          </span>
          <span
            style={{
              fontSize: 12,
              color: V3.w3,
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform .2s',
            }}
          >
            ▾
          </span>
        </div>
      </div>
      {open && (
        <div
          style={{
            padding: '0 20px 16px',
            borderTop: '1px solid ' + V3.pur + '05',
            paddingTop: 14,
          }}
        >
          {children}
        </div>
      )}
    </div>
  )
}

// ----------------------------------------------------------------
// EmptyTile — honest state when data is not available.
// ----------------------------------------------------------------

export function EmptyTile({
  title,
  body,
  kind = 'empty',
}: {
  title: string
  body: string
  kind?: 'empty' | 'unavailable' | 'auth' | 'instructional'
}) {
  const color =
    kind === 'unavailable'
      ? V3.red
      : kind === 'auth'
      ? V3.gld
      : kind === 'instructional'
      ? V3.blue
      : V3.w3

  return (
    <div
      style={{
        padding: '28px 24px',
        borderRadius: 14,
        background: V3.pur + '02',
        border: '1px dashed ' + color + '30',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          fontSize: 9,
          fontFamily: V3.mono,
          fontWeight: 700,
          color,
          letterSpacing: 2,
          marginBottom: 6,
        }}
      >
        {kind === 'unavailable'
          ? 'PROVIDER UNAVAILABLE'
          : kind === 'auth'
          ? 'SIGN IN REQUIRED'
          : kind === 'instructional'
          ? 'WALKTHROUGH'
          : 'NO DATA'}
      </div>
      <div style={{ fontSize: 14, fontWeight: 500, color: V3.w, marginBottom: 4 }}>
        {title}
      </div>
      <div style={{ fontSize: 11, color: V3.w3 }}>{body}</div>
    </div>
  )
}

// ----------------------------------------------------------------
// Global keyframes — injected once at the page root.
// ----------------------------------------------------------------

export function V3Styles() {
  return (
    <style>{`
      @keyframes hv3Pulse { 0%,100%{opacity:.4} 50%{opacity:1} }
      @keyframes hv3ScanX { from{transform:translateX(-100%)} to{transform:translateX(200%)} }
      @keyframes hv3GridDrift { from{background-position:0 0} to{background-position:0 50px} }
      @keyframes hv3FadeIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:none} }
    `}</style>
  )
}
